import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Helpers
// ============================================================================

function normalizeUrl(raw: string | null | undefined): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  return trimmed;
}

// ============================================================================
// Slot group definitions (module-level literals — extractor follows one level)
//
// Note: the slot keys `secondary_cta` and `phone` are preserved from the
// previous iteration so any per-site overrides keep resolving. Their
// intent has changed: `secondary_cta` is now the primary "Apply Now"
// button, and `phone` is now a text-style "Contact Leasing" link.
// ============================================================================

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Apply Now CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Contact Leasing Link',
  fields: {
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Contact Leasing',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function PreferredEmployerCTA() {
  const applyCta = useSlotGroup('preferredemployercta.secondary_cta', secondaryCtaDef);
  const contactLink = useSlotGroup('preferredemployercta.phone', phoneLinkDef);

  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const applyUrl = normalizeUrl(applyCta.url);
  const contactUrl = normalizeUrl(contactLink.url);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pep-cta-section"
    >
      <div className="pep-cta-inner">
        <div className="pep-cta-content">
          <RichTextContent
            path="preferredemployercta.eyebrow"
            as="span"
            className="pep-cta-eyebrow"
          />
          <RichTextContent
            path="preferredemployercta.heading"
            as="h2"
            className="pep-cta-heading"
          />
          <RichTextContent
            path="preferredemployercta.subheading"
            as="div"
            className="pep-cta-subheading"
          />
        </div>

        <div className="pep-cta-actions">
          <EditableSlotGroup
            groupPath="preferredemployercta.secondary_cta"
            definition={secondaryCtaDef}
          >
            <a
              href={applyUrl || '#'}
              target={applyCta._target || '_self'}
              className="pep-cta-btn vantage-cta-sheen"
            >
              <span className="relative z-10">{applyCta.label || 'Apply Now'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="preferredemployercta.phone"
            definition={phoneLinkDef}
          >
            <a
              href={contactUrl || '/contact'}
              target={contactLink._target || '_self'}
              className="pep-cta-btn pep-cta-btn--on-dark vantage-cta-sheen"
            >
              <span className="relative z-10">{contactLink.label || 'Contact Leasing'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pep-cta-section {
          padding: 3rem 1rem;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
        }
        @media (min-width: 768px) {
          .pep-cta-section {
            padding: 4.5rem 1.5rem;
          }
        }

        .pep-cta-inner {
          max-width: 60rem;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
          text-align: center;
        }

        .pep-cta-eyebrow {
          display: block;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 75%, transparent);
          margin-bottom: 0.6rem;
        }
        .pep-cta-eyebrow p,
        .pep-cta-eyebrow span {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          text-transform: inherit;
          margin: 0;
        }

        .pep-cta-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.85rem;
          font-weight: 500;
          line-height: 1.15;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text-on-primary, #fff);
          margin: 0 0 0.85rem;
          text-wrap: balance;
        }
        @media (min-width: 768px) {
          .pep-cta-heading {
            font-size: 2.5rem;
          }
        }
        .pep-cta-heading p,
        .pep-cta-heading span {
          font: inherit;
          color: inherit;
          margin: 0;
        }

        .pep-cta-subheading {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 85%, transparent);
          max-width: 40rem;
          margin: 0 auto;
          text-wrap: balance;
        }
        .pep-cta-subheading p { margin: 0 0 0.6rem; }
        .pep-cta-subheading p:last-child { margin-bottom: 0; }
        .pep-cta-subheading a {
          color: var(--vibe-color-text-on-primary, #fff);
          text-decoration: underline;
        }

        .pep-cta-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 0.75rem 1rem;
        }

        /* Body-scale CTA — matches the sizing of .po-cta / .pet-cta so the
           site's CTAs feel like one family. .vantage-cta-sheen (from
           useCtaSheenStyles) handles fill + hover streak.
           min-width pins both buttons to the same visual width so
           short labels (Apply Now) don't render narrower than longer
           siblings (Contact Leasing); a longer author-set label can
           still grow past it. */
        .pep-cta-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          padding: 0.9rem 1.75rem;
          min-height: 46px;
          min-width: 13rem;
          /* Apply Now (secondary_cta) uses secondary on the primary band. */
          --vantage-cta-sheen-accent: var(--vibe-color-secondary);
          --vantage-cta-sheen-on-accent: var(--vibe-color-text-on-secondary);
        }

        /* On-dark variant — overrides the sheen accent so the second CTA
           reads as white-on-dark instead of secondary-on-dark. Uses the sheen
           hook's documented override vars, so hover, sweep, and shadow all
           still work. */
        .pep-cta-btn--on-dark {
          --vantage-cta-sheen-accent: #fff;
          --vantage-cta-sheen-on-accent: var(--vibe-color-text);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerCTA',
  displayName: 'Preferred Employer — CTA',
  tokens: {
    'preferredemployercta.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Ready to Move In?</p>',
    },
    'preferredemployercta.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Take advantage of your Preferred Employer benefits at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'preferredemployercta.subheading': {
      label: 'Subheading',
      type: 'richtext',
      defaultValue:
        '<p>Apply online today or reach out to our leasing team to verify eligibility and confirm the latest move-in offer.</p>',
    },
  },
  slotGroups: {
    'preferredemployercta.secondary_cta': secondaryCtaDef,
    'preferredemployercta.phone': phoneLinkDef,
  },
};

(PreferredEmployerCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerCTA;
